import React, { useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';

// COMPONENTES REUTILIZABLES DEL TABLERO DE GESTIÓN
import TableContainer from '../../TablerosGestion/common/TableContainer.jsx';
import SortableHeader from '../../TablerosGestion/common/SortableHeader';
import ColumnSelector from '../../TablerosGestion/common/ColumnSelector';
import PaginatorFooter from '../../TablerosGestion/common/PaginatorFooter';
import FiltrosDinamicos from '../../../ui/FiltrosDinamicos.jsx';

// MODALES EXISTENTES
import ModalGastoCompra from '../Modales/ModalGastoCompra';
import ModalDetalleGastos from '../Modales/ModalDetalleGastos';
import ModalConfirmacion from '../../../ui/ModalConfirmacion';

// HOOK DE NEGOCIO Y HELPERS
import { useGastosComprasTable } from '../Tabs/Hooks/useGastosComprasTable';
import { formatCurrency, getStatusBadgeStyle, formatDate } from '../helpers';

const IconArrowLeft = () => (
    <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
    </svg>
);

const TableIcons = {
    Eye: () => (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
    ),
    Edit: () => (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
    ),
    Trash: () => (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-4v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
    )
};

// --- FILA MEMOIZADA CÓMODA Y ESPACIOSA ---
const TableRow = memo(({ item, columnVisibility, onViewDetail, onEdit, onDelete }) => {
    const monedaSimbolo = item.moneda === 'USD' ? 'USD' : 'ARS';

    return (
        <tr className="group transition-all duration-75 border-b border-slate-100 hover:bg-rose-50/20 bg-white">
            
            {/* FECHA GASTO / INGRESO */}
            {columnVisibility.fechaGasto && (
                <td className="py-1.5 px-2.5 border-b border-r border-slate-100 text-center font-mono text-slate-600 whitespace-nowrap">
                    <div className="text-[12px] font-bold text-slate-800 text-xs">{formatDate(item.fechaGasto)}</div>
                    <div className="text-[11px] text-slate-400">Ing: {formatDate(item.fechaIngreso || item.fechaGasto)}</div>
                </td>
            )}

            {/* FECHA VENCIMIENTO */}
            {columnVisibility.fechaVencimientoGasto && (
                <td className="py-1.5 px-2.5 border-b border-r border-slate-100 text-center font-mono whitespace-nowrap">
                    <div className="font-bold text-rose-900 text-xs">{formatDate(item.fechaVencimientoGasto || item.fechaGasto)}</div>
                </td>
            )}

            {/* COMPROBANTE */}
            {columnVisibility.numeroComprobanteDisplay && (
                <td className="py-1.5 px-2.5 border-b border-r border-slate-100 font-mono text-center whitespace-nowrap">
                    {item.esImpuesto ? (
                        <span className="bg-indigo-50 text-indigo-900 font-extrabold px-2 py-0.5 rounded text-[10px] border border-indigo-200 inline-block uppercase">
                            🏛️ Impuesto
                        </span>
                    ) : (
                        <div className="flex flex-col items-center justify-center">
                            <span className="font-bold text-slate-900 text-xs whitespace-nowrap">{item.tipoComprobanteDisplay}</span>
                            <span className="text-[10px] text-rose-600 font-bold whitespace-nowrap">{item.numeroComprobanteDisplay}</span>
                        </div>
                    )}
                </td>
            )}

            {/* PROVEEDOR / FISCO */}
            {columnVisibility.proveedorNombreDisplay && (
                <td className="py-1.5 px-2.5 text-xs text-slate-900 font-bold border-b border-r border-slate-100 text-left uppercase">
                    <div className="flex items-center justify-between gap-2">
                        <span className="truncate max-w-[280px]" title={item.proveedorNombreDisplay}>{item.proveedorNombreDisplay}</span>
                        {item.tieneDesfasaje && (
                            <span 
                                className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-black cursor-help ${
                                    item.esFaltante ? 'bg-rose-100 text-rose-700 border border-rose-200' : 'bg-amber-100 text-amber-800 border border-amber-200'
                                }`}
                                title={item.esFaltante ? `⚠️ Pendiente de abonar: ${formatCurrency(item.diferencia, monedaSimbolo)}` : `⚠️ Excedente debitado: ${formatCurrency(Math.abs(item.diferencia), monedaSimbolo)}`}
                            >
                                ⚠️ {item.esFaltante ? 'PENDIENTE' : 'EXCEDENTE'}
                            </span>
                        )}
                    </div>
                </td>
            )}

            {/* CONCEPTO */}
            {columnVisibility.conceptoDisplay && (
                <td className="py-1.5 px-2.5 border-b border-r border-slate-100 text-left font-bold text-slate-900 uppercase text-xs whitespace-nowrap">
                    {item.conceptoDisplay}
                </td>
            )}

            {/* SUBCONCEPTO */}
            {columnVisibility.subconceptoDisplay && (
                <td className="py-1.5 px-2.5 border-b border-r border-slate-100 text-left whitespace-nowrap">
                    {item.subconceptoNombre ? (
                        <span className="bg-indigo-50 text-indigo-800 font-black px-2 py-0.5 rounded text-[10px] uppercase border border-indigo-100 inline-block truncate max-w-[200px]" title={item.subconceptoNombre}>
                            {item.subconceptoNombre}
                        </span>
                    ) : (
                        <span className="text-slate-400 italic text-[11px]">S/D</span>
                    )}
                </td>
            )}

            {/* ÁREA / CENTRO DE COSTOS */}
            {columnVisibility.areaNombreDisplay && (
                <td className="py-1.5 px-2.5 border-b border-r border-slate-100 text-left whitespace-nowrap">
                    {item.areaNombre ? (
                        <span className="bg-blue-50 text-blue-800 font-black px-2 py-0.5 rounded text-[10px] uppercase border border-blue-100 inline-block truncate max-w-[180px]" title={item.areaNombre}>
                            {item.areaNombre}
                        </span>
                    ) : (
                        <span className="text-slate-400 italic text-[11px]">S/A</span>
                    )}
                </td>
            )}

            {/* MONTO NETO */}
            {columnVisibility.montoNeto && (
                <td className="py-1.5 px-2.5 border-b border-r border-slate-100 text-right font-mono text-slate-600 whitespace-nowrap font-semibold text-sm">
                    {formatCurrency(item.montoNeto, monedaSimbolo)}
                </td>
            )}

            {/* MONTO IVA */}
            {columnVisibility.montoIva && (
                <td className="py-1.5 px-2.5 border-b border-r border-slate-100 text-right font-mono font-bold text-slate-800 text-sm whitespace-nowrap">
                    {item.esImpuesto ? formatCurrency(0, monedaSimbolo) : formatCurrency(item.montoIva, monedaSimbolo)}
                </td>
            )}

            {/* MONTO TOTAL */}
            {columnVisibility.montoTotal && (
                <td className="py-1.5 px-2.5 border-b border-r border-slate-100 text-right font-mono font-black text-slate-900 text-sm whitespace-nowrap">
                    {formatCurrency(item.montoTotal, monedaSimbolo)}
                </td>
            )}

            {/* EGRESO BANCO */}
            {columnVisibility.egresoEfectivoBanco && (
                <td className="py-1.5 px-2.5 border-b border-r border-slate-100 text-right font-mono text-sm whitespace-nowrap">
                    {item.tieneRegistroBanco || item.egresoEfectivoBanco > 0 ? (
                        <div className="flex flex-col items-end">
                            <span className={`font-black ${
                                item.esConciliado 
                                    ? 'text-emerald-700' 
                                    : item.esFaltante 
                                        ? 'text-rose-600' 
                                        : 'text-amber-600'
                            }`}>
                                {formatCurrency(item.egresoEfectivoBanco, monedaSimbolo)}
                            </span>
                            {item.tieneDesfasaje && (
                                <span className={`text-[10px] font-extrabold uppercase px-1 rounded ${
                                    item.esFaltante ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-800'
                                }`}>
                                    {item.esFaltante ? `-${formatCurrency(item.diferencia, monedaSimbolo)}` : `+${formatCurrency(Math.abs(item.diferencia), monedaSimbolo)}`}
                                </span>
                            )}
                        </div>
                    ) : (
                        <span className="text-slate-300 font-bold italic text-xs">{formatCurrency(0, monedaSimbolo)}</span>
                    )}
                </td>
            )}

            {/* ESTATUS */}
            {columnVisibility.estatus && (
                <td className="py-1.5 px-2.5 border-b border-r border-slate-100 text-center whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-[9px] font-bold uppercase border ${getStatusBadgeStyle(item.estatus)}`}>
                        {item.estatus}
                    </span>
                </td>
            )}

            {/* ACCIONES DE FILA */}
            <td className="py-1.5 px-2.5 text-right sticky right-0 transition-colors z-[45] border-b border-l border-slate-100 bg-white group-hover:bg-slate-50 shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.05)]">
                <div className="flex justify-end items-center gap-1">
                    <button onClick={() => onViewDetail(item)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all cursor-pointer" title="Ver Detalle"><TableIcons.Eye /></button>
                    <button onClick={() => onEdit(item)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all cursor-pointer" title="Editar Comprobante"><TableIcons.Edit /></button>
                    <button onClick={() => onDelete(item)} className="p-1.5 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all cursor-pointer" title="Eliminar Comprobante"><TableIcons.Trash /></button>
                </div>
            </td>
        </tr>
    );
});
TableRow.displayName = 'TableRow';

// --- COMPONENTE PRINCIPAL DE GASTOS Y COMPRAS ---
const GastosComprasTab = ({ maestros = {}, onBack }) => {
    const navigate = useNavigate();
    const { state, actions, tableState } = useGastosComprasTable();

    const {
        loading,
        showModal,
        editingGasto,
        gastoToDelete,
        gastoToView,
        itemsTable
    } = state;

    const {
        setShowModal,
        setGastoToDelete,
        setGastoToView,
        handleOpenCreate,
        handleOpenEdit,
        handleDelete
    } = actions;

    const {
        sortConfig,
        columnVisibility,
        currentPage,
        filterValues,
        filteredData,
        paginatedData,
        totalPages,
        handleSort,
        toggleColumn,
        handleFilterChange,
        setCurrentPage
    } = tableState;

    const handleVolver = () => {
        if (onBack) {
            onBack();
        } else {
            navigate('/admin/facturacion-gestion');
        }
    };

    const columnLabels = {
        fechaGasto: "Gasto / Ingreso",
        fechaVencimientoGasto: "Vencimiento",
        numeroComprobanteDisplay: "Comprobante",
        proveedorNombreDisplay: "Proveedor / Fisco",
        conceptoDisplay: "Concepto",
        subconceptoDisplay: "Subconcepto",
        areaNombreDisplay: "Área / C. Costo",
        montoNeto: "Monto Neto",
        montoIva: "Monto IVA",
        montoTotal: "Monto Total",
        egresoEfectivoBanco: "Egreso Banco",
        estatus: "Estatus"
    };

    const filterConfig = useMemo(() => {
        const getUnique = (key, data = itemsTable) => [...new Set(data.map(p => p[key]))].filter(Boolean).sort().map(v => ({ label: String(v), value: String(v) }));

        return [
            { name: 'search', label: 'Buscador Global', type: 'text', placeholder: 'Buscar proveedor, concepto, N° comprobante...', className: 'w-80' },
            { name: 'periodoDisplay', label: 'Período', type: 'select', isMulti: true, options: getUnique('periodoDisplay') },
            { name: 'estatus', label: 'Estatus', type: 'select', isMulti: true, options: getUnique('estatus') },
            { name: 'areaNombreDisplay', label: 'Área / C. Costo', type: 'select', isMulti: true, options: getUnique('areaNombreDisplay') },
            { name: 'moneda', label: 'Moneda', type: 'select', isMulti: true, options: getUnique('moneda') }
        ];
    }, [itemsTable]);

    if (loading) return (
        <div className="flex h-full w-full flex-col items-center justify-center bg-white space-y-6 text-left min-h-[400px]">
            <div className="w-16 h-16 border-4 border-rose-600/20 border-t-rose-600 rounded-full animate-spin"></div>
            <div className="flex flex-col items-center gap-2 text-center text-slate-900">
                <span className="font-black text-[10px] uppercase tracking-[0.3em] text-rose-600 animate-pulse">Sincronizando Gastos & Compras</span>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-8">Cargando registros operativos, proveedores y egresos...</span>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-full w-full overflow-hidden bg-white font-['Inter'] antialiased text-left text-slate-900">
            
            {/* CABECERA Y ACCIONES SUPERIORES */}
            <header className="flex h-20 shrink-0 items-center justify-between border-b border-slate-200 px-8 bg-white z-[110]">
                <div className="flex items-center gap-6 text-left">
                    <button 
                        type="button"
                        onClick={handleVolver} 
                        className="p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-all border border-transparent hover:border-slate-200 shadow-sm cursor-pointer"
                        title="Volver"
                    >
                        <IconArrowLeft />
                    </button>
                    <h2 className="text-xl font-bold text-slate-800 tracking-tight text-left">
                        Centro de Gestión Financiera Gastos & Compras 
                    </h2>
                </div>

                <div className="flex items-center gap-4 text-left">
                    <button
                        type="button"
                        onClick={handleOpenCreate}
                        className="px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-black uppercase text-xs tracking-wider shadow-md shadow-rose-100 active:scale-95 transition-all cursor-pointer flex items-center gap-1.5"
                    >
                        + Nuevo Gasto / Compra / Impuesto
                    </button>
                    
                    <ColumnSelector 
                        columnVisibility={columnVisibility} 
                        columnLabels={columnLabels} 
                        toggleColumn={toggleColumn} 
                    />

                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-1 pr-4 shadow-sm text-left">
                        <div className="bg-rose-600 text-white px-4 py-1.5 rounded-lg text-lg font-black tabular-nums">
                            {filteredData.length}
                        </div>
                        <span className="text-[10px] font-bold uppercase text-slate-500 text-left">Egresos</span>
                    </div>
                </div>
            </header>

            {/* FILTROS DINÁMICOS */}
            <FiltrosDinamicos 
                key="gastos-compras-filters"
                config={filterConfig} 
                onFilterChange={handleFilterChange} 
                defaultValues={filterValues} 
            />

            {/* TABLA PRINCIPAL RESPONSIVE - AMPLIADA PARA DAR AIRE */}
            <main className="flex-grow overflow-hidden flex flex-col bg-white min-h-0">
                <TableContainer minWidth="2200px">
                    <thead>
                        <tr className="bg-[#01032d] text-white text-left">
                            <SortableHeader label="Gasto / Ingreso" sortKey="fechaGasto" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-36 text-center" />
                            <SortableHeader label="Vencimiento" sortKey="fechaVencimientoGasto" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Comprobante" sortKey="numeroComprobanteDisplay" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-36 text-center" />
                            <SortableHeader label="Proveedor / Fisco" sortKey="proveedorNombreDisplay" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[300px]" />
                            <SortableHeader label="Concepto" sortKey="conceptoDisplay" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-44" />
                            <SortableHeader label="Subconcepto" sortKey="subconceptoDisplay" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48" />
                            <SortableHeader label="Área / C. Costo" sortKey="areaNombreDisplay" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-44" />
                            <SortableHeader label="Monto Neto" sortKey="montoNeto" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-36 text-right" />
                            <SortableHeader label="Monto IVA" sortKey="montoIva" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-right" />
                            <SortableHeader label="Monto Total" sortKey="montoTotal" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-36 text-right" />
                            <SortableHeader label="Egreso Banco" sortKey="egresoEfectivoBanco" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-36 text-right" />
                            <SortableHeader label="Estatus" sortKey="estatus" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <th className="w-28 p-3 sticky right-0 top-0 bg-slate-900 border-b border-slate-700 z-[65] uppercase tracking-wider text-[10px] font-semibold text-center text-white shadow-[-2px_0_5px_rgba(0,0,0,0.2)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-left">
                        {paginatedData.length === 0 ? (
                            <tr>
                                <td colSpan={13} className="p-16 text-center text-slate-400 italic font-semibold">
                                    No se encontraron comprobantes de egresos para los filtros aplicados.
                                </td>
                            </tr>
                        ) : (
                            paginatedData.map(item => (
                                <TableRow
                                    key={item.id}
                                    item={item}
                                    columnVisibility={columnVisibility}
                                    onViewDetail={(gasto) => setGastoToView(gasto)}
                                    onEdit={handleOpenEdit}
                                    onDelete={(itemToDelete) => setGastoToDelete(itemToDelete)}
                                />
                            ))
                        )}
                    </tbody>
                </TableContainer>

                {/* PIE DE PAGINACIÓN UNIFICADO Y PEGADO ABAJO */}
                <PaginatorFooter 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    currentLength={paginatedData.length}
                    totalLength={filteredData.length}
                    onPageChange={setCurrentPage}
                    label="egresos"
                    accentClass="text-rose-900 bg-rose-50 border-rose-100"
                />
            </main>

            {/* MODALES */}
            <ModalGastoCompra
                show={showModal}
                onClose={() => setShowModal(false)}
                onSuccess={() => {}}
                initialData={editingGasto}
                maestros={maestros}
            />

            <ModalDetalleGastos
                show={!!gastoToView}
                onClose={() => setGastoToView(null)}
                gasto={gastoToView}
            />

            <ModalConfirmacion
                show={!!gastoToDelete}
                onClose={() => setGastoToDelete(null)}
                onConfirm={handleDelete}
                title="Eliminar Comprobante / Impuesto"
                message={`¿Está seguro de eliminar el registro de ${gastoToDelete?.proveedorNombre} por ${formatCurrency(gastoToDelete?.montoTotal || gastoToDelete?.gastoFinal, gastoToDelete?.moneda || 'ARS')}?`}
            />
        </div>
    );
};

export default GastosComprasTab;